import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import AccountingToolPage from '@/components/AccountingToolPage'
import UnderlineTabNav from '@/components/UnderlineTabNav'
import WarningBanner from '@/components/WarningBanner'
import { useAuth } from '@/contexts/AuthContext'
import { usePageAccess } from '@/hooks/usePageAccess'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { DrawerShell } from '@/tableControls'
import { toast } from '@/store/toastStore'
import {
  exportCase,
  formatApiError,
  isExportConflict,
  listRollForwardRuns,
  type ExportConflictCode,
  type ExportDryRunResponse,
  type RollForwardSummary,
  type ValidationItem,
} from '@/services/projections'
import type { View } from '@/pages/projections/engine/types'
import {
  thresholdsFromSettings,
  useProjectionsStore,
  recipeLineCount,
  validationOptionsFromModel,
  type HistoryMonths,
  type ProjectionsView,
} from '@/store/projectionsStore'
import ConcurrencyDialog from './projections/ConcurrencyDialog'
import DeleteCaseDialog from './projections/DeleteCaseDialog'
import DiscardConfirm from './projections/DiscardConfirm'
import DuplicateCaseDialog from './projections/DuplicateCaseDialog'
import EditCaseDialog from './projections/EditCaseDialog'
import CashPolicyModal from './projections/cashPolicy/CashPolicyModal'
import { invalidateFloorAnalysisCache } from './projections/cashPolicy/floorAnalysisCache'
import ScheduleHealedBanner from './projections/cashPolicy/ScheduleHealedBanner'
import KpiStrip from './projections/KpiStrip'
import ModelBar from './projections/ModelBar'
import NewCaseDialog from './projections/NewCaseDialog'
import OneTimeItems from './projections/OneTimeItems'
import ProjectionsGrid, { type ProjectionsGridHandle } from './projections/ProjectionsGrid'
import ExportConfirmDialog from './projections/ExportConfirmDialog'
import HoldProjectedValuesDialog from './projections/HoldProjectedValuesDialog'
import RollForwardResultDialog, {
  type RollForwardChoice,
} from './projections/RollForwardResultDialog'
import ProjectionAccuracyModal from './projections/ProjectionAccuracyModal'
import CaseSummaryModal from './projections/summary/CaseSummaryModal'
import NotePopover from './projections/NotePopover'
import SaveValidationDialog from './projections/SaveValidationDialog'
import UploadCaseDialog from './projections/UploadCaseDialog'
import OverrideCaseDialog from './projections/OverrideCaseDialog'
import type { ParsedCaseFile } from './projections/caseJson'
import { uploadValidationIsClean } from './projections/uploadCaseHandOff'
import { validateCase } from './projections/projectionsValidation'
import { gridBandSectionKeys } from './projections/buildGridRows'
import GridCompareMenu from './projections/GridCompareMenu'
import GridViewMenu from './projections/GridViewMenu'
import { monthKeyFromT } from './projections/engine/calendar'
import { getVisibleTimeIndices, monthLabel } from './projections/monthColumns'
import { formatCurrency, formatMonthLabel } from './projections/projectionsFormat'

type PendingAction = { type: 'selectCase'; caseId: number } | { type: 'uploadJson' }

const VIEW_TABS = [
  { id: 'river_oaks', label: 'River Oaks' },
  { id: 'highland_park', label: 'Highland Park' },
  { id: 'ga', label: 'G&A' },
  { id: 'one_time_items', label: 'One-time items' },
  { id: 'consolidated', label: 'Consolidated' },
]

export default function Projections() {
  const { user } = useAuth()
  const { canEdit: pageCanEdit } = usePageAccess('projections')
  const gridRef = useRef<ProjectionsGridHandle>(null)

  const schema = useProjectionsStore((s) => s.schema)
  const history = useProjectionsStore((s) => s.history)
  const model = useProjectionsStore((s) => s.model)
  const cases = useProjectionsStore((s) => s.cases)
  const selectedCaseId = useProjectionsStore((s) => s.selectedCaseId)
  const selectedCase = useProjectionsStore((s) => s.selectedCase)
  const saved = useProjectionsStore((s) => s.saved)
  const draft = useProjectionsStore((s) => s.draft)
  const dirty = useProjectionsStore((s) => s.dirty)
  const evaluated = useProjectionsStore((s) => s.evaluated)
  const view = useProjectionsStore((s) => s.view)
  const historyMonths = useProjectionsStore((s) => s.historyMonths)
  const loading = useProjectionsStore((s) => s.loading)
  const switchingCase = useProjectionsStore((s) => s.switchingCase)
  const saving = useProjectionsStore((s) => s.saving)
  const importing = useProjectionsStore((s) => s.importing)
  const importBlock = useProjectionsStore((s) => s.importBlock)
  const importDrift = useProjectionsStore((s) => s.importDrift)
  const loadError = useProjectionsStore((s) => s.loadError)
  const notes = useProjectionsStore((s) => s.notes)
  const schemaStale = useProjectionsStore((s) => s.schemaStale)
  const compareMode = useProjectionsStore((s) => s.compareMode)
  const compareColumns = useProjectionsStore((s) => s.compareColumns)
  const compareModel = useProjectionsStore((s) => s.compareModel)
  const snapshots = useProjectionsStore((s) => s.snapshots)

  const load = useProjectionsStore((s) => s.load)
  const selectCase = useProjectionsStore((s) => s.selectCase)
  const createCase = useProjectionsStore((s) => s.createCase)
  const duplicateCase = useProjectionsStore((s) => s.duplicateCase)
  const updateCaseMeta = useProjectionsStore((s) => s.updateCaseMeta)
  const deleteCase = useProjectionsStore((s) => s.deleteCase)
  const setView = useProjectionsStore((s) => s.setView)
  const setHistoryMonths = useProjectionsStore((s) => s.setHistoryMonths)
  const setDriverType = useProjectionsStore((s) => s.setDriverType)
  const setDriverParams = useProjectionsStore((s) => s.setDriverParams)
  const setPctOfLineRef = useProjectionsStore((s) => s.setPctOfLineRef)
  const addOneTimeItem = useProjectionsStore((s) => s.addOneTimeItem)
  const updateOneTimeItem = useProjectionsStore((s) => s.updateOneTimeItem)
  const removeOneTimeItem = useProjectionsStore((s) => s.removeOneTimeItem)
  const setValue = useProjectionsStore((s) => s.setValue)
  const applyFromColumn = useProjectionsStore((s) => s.applyFromColumn)
  const clearOverride = useProjectionsStore((s) => s.clearOverride)
  const setOverrideReason = useProjectionsStore((s) => s.setOverrideReason)
  const setEditorName = useProjectionsStore((s) => s.setEditorName)
  const clearDriverValues = useProjectionsStore((s) => s.clearDriverValues)
  const setCompareMode = useProjectionsStore((s) => s.setCompareMode)
  const toggleCompareColumn = useProjectionsStore((s) => s.toggleCompareColumn)

  useEffect(() => {
    const name = user?.name || user?.email || null
    setEditorName(name)
  }, [user?.email, user?.name, setEditorName])
  const toggleConsolidatedOnly = useProjectionsStore((s) => s.toggleConsolidatedOnly)
  const discard = useProjectionsStore((s) => s.discard)
  const save = useProjectionsStore((s) => s.save)
  const importFromCgm = useProjectionsStore((s) => s.importFromCgm)
  const clearImportDrift = useProjectionsStore((s) => s.clearImportDrift)
  const reloadFromServer = useProjectionsStore((s) => s.reloadFromServer)
  const adoptServerTimestamp = useProjectionsStore((s) => s.adoptServerTimestamp)
  const upsertNote = useProjectionsStore((s) => s.upsertNote)
  const refreshExports = useProjectionsStore((s) => s.refreshExports)
  const scheduleHealedNotice = useProjectionsStore((s) => s.scheduleHealedNotice)
  const refreshStaleSchedule = useProjectionsStore((s) => s.refreshStaleSchedule)
  const setCashPolicy = useProjectionsStore((s) => s.setCashPolicy)
  const loadCaseFromFile = useProjectionsStore((s) => s.loadCaseFromFile)
  const rollForward = useProjectionsStore((s) => s.rollForward)
  const revertImport = useProjectionsStore((s) => s.revertImport)
  const reanchorRecipes = useProjectionsStore((s) => s.reanchorRecipes)
  const holdPreRollValues = useProjectionsStore((s) => s.holdPreRollValues)

  const [showNewCase, setShowNewCase] = useState(false)
  const [cashPolicyOpen, setCashPolicyOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(false)
  const [healRefreshing, setHealRefreshing] = useState(false)
  const [newCaseError, setNewCaseError] = useState<string | null>(null)
  const [showDuplicateCase, setShowDuplicateCase] = useState(false)
  const [duplicateCaseError, setDuplicateCaseError] = useState<string | null>(null)
  const [showDeleteCase, setShowDeleteCase] = useState(false)
  const [showEditCase, setShowEditCase] = useState(false)
  const [editCaseError, setEditCaseError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [overrideParsed, setOverrideParsed] = useState<ParsedCaseFile | null>(null)
  const [overrideFileName, setOverrideFileName] = useState<string | null>(null)
  const uploadHandOffRef = useRef<{ fileName: string; wasCreate: boolean } | null>(null)
  const [validationOpen, setValidationOpen] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationItem[]>([])
  const [validationWarnings, setValidationWarnings] = useState<ValidationItem[]>([])
  const [concurrencyOpen, setConcurrencyOpen] = useState(false)
  const [noteTarget, setNoteTarget] = useState<{
    cellKey: string
    position: { x: number; y: number }
    context: string
    variant?: 'cell' | 'line'
    computedMethodology?: string | null
    recipeRationale?: string
  } | null>(null)
  const [exportDryRun, setExportDryRun] = useState<ExportDryRunResponse | null>(null)
  const [exportConflictCode, setExportConflictCode] = useState<ExportConflictCode | null>(null)
  const [exportConflict, setExportConflict] = useState<string | null>(null)
  const [exportConflictDiff, setExportConflictDiff] = useState<
    import('@/services/projections').SheetLayoutDriftItem[] | null
  >(null)
  const [exporting, setExporting] = useState(false)
  const [rollForwardSession, setRollForwardSession] = useState<{
    summary: RollForwardSummary
    lastClosedMonth: string
    choice: RollForwardChoice
  } | null>(null)
  const [rollForwardDialogOpen, setRollForwardDialogOpen] = useState(false)
  const [discardImportConfirmOpen, setDiscardImportConfirmOpen] = useState(false)
  const [reanchorConfirmOpen, setReanchorConfirmOpen] = useState(false)
  const [holdValuesOpen, setHoldValuesOpen] = useState(false)
  const [accuracyOpen, setAccuracyOpen] = useState(false)

  useEffect(() => {
    void load()
  }, [load])

  useUnsavedChangesGuard(dirty)

  const selectedListItem = cases.find((c) => c.id === selectedCaseId)
  const caseExpired = selectedListItem?.expired ?? false
  const caseEditable = pageCanEdit && !caseExpired
  const needsRollForward =
    selectedCase != null &&
    model?.latest_import?.id != null &&
    selectedCase.base_import_id !== model.latest_import.id
  const importMatchesCase = !needsRollForward
  const exportEnabled =
    caseEditable &&
    !dirty &&
    selectedCaseId != null &&
    importMatchesCase &&
    !schemaStale &&
    selectedCase?.outputs_stale !== true
  const exportDisabledTitle = !caseEditable
    ? caseExpired
      ? 'Case is expired — edit expiration to re-enable'
      : 'Editing is disabled'
    : dirty
      ? 'Save changes before exporting'
      : selectedCaseId == null
        ? 'Select a case to export'
        : schemaStale
          ? 'Run Update Historicals first (sheet layout changed)'
          : selectedCase?.outputs_stale === true
            ? 'Save the case first (rolled forward to new actuals)'
            : needsRollForward
              ? 'Save the case against the latest import first'
              : undefined

  const lastClosedMonth = model?.latest_import?.last_closed_month ?? history?.lastClosedMonth ?? ''

  const visibleSnapshotMonths = useMemo(() => {
    const snapMonths = selectedCase?.snapshot_months ?? []
    if (!lastClosedMonth || snapMonths.length === 0) return []
    const { histTs } = getVisibleTimeIndices(history?.nHistory ?? 0, historyMonths)
    const visible = new Set(histTs.map((t) => monthKeyFromT(lastClosedMonth, t)))
    return snapMonths.filter((m) => visible.has(m)).sort()
  }, [selectedCase?.snapshot_months, lastClosedMonth, history?.nHistory, historyMonths])
  const visibleSnapshotCount = visibleSnapshotMonths.length
  const hasSnapshots = (selectedCase?.snapshot_months.length ?? 0) > 0

  const gridCompare =
    compareMode === 'on' && compareModel
      ? {
          model: compareModel,
          columns: compareColumns,
          caseName: selectedCase?.name ?? '',
          onToggleColumn: toggleCompareColumn,
          sourceByMonth: Object.fromEntries(
            (snapshots ?? []).map((s) => [
              s.month,
              { caseSavedAt: s.case_saved_at, capturedAt: s.captured_at },
            ]),
          ),
        }
      : null

  const cashPolicyBreachMessage = useMemo(() => {
    if (draft.cash_policy?.enabled !== true) return null
    const summary = evaluated?.cash_policy?.summary
    if (!summary || summary.breach_months.length === 0) return null
    const count = summary.breach_months.length
    let worstShortfall = 0
    let worstMonth = ''
    for (const t of summary.breach_months) {
      const month = evaluated?.cash_policy?.months.find((row) => row.t === t)
      const shortfall = month?.shortfall ?? 0
      if (shortfall >= worstShortfall) {
        worstShortfall = shortfall
        worstMonth = monthLabel(lastClosedMonth, t)
      }
    }
    return `Cash policy floor is breached in ${count} projected month${count === 1 ? '' : 's'} (worst shortfall ${formatCurrency(worstShortfall)} in ${worstMonth}). Open Cash policy for details.`
  }, [draft.cash_policy?.enabled, evaluated, lastClosedMonth])

  const showScheduleHealRefresh = Boolean(
    scheduleHealedNotice && !scheduleHealedNotice.healed,
  )

  const refreshHealedSchedule = useCallback(async () => {
    setHealRefreshing(true)
    try {
      await refreshStaleSchedule()
    } catch (error) {
      toast.error(formatApiError(error, 'Waterfall schedule failed'))
    } finally {
      setHealRefreshing(false)
    }
  }, [refreshStaleSchedule])

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('projections-collapsed-sections')
      if (!raw) return new Set()
      const parsed = JSON.parse(raw) as string[]
      return new Set(parsed)
    } catch {
      return new Set()
    }
  })

  const persistCollapsedSections = useCallback((next: Set<string>) => {
    localStorage.setItem('projections-collapsed-sections', JSON.stringify([...next]))
  }, [])

  const toggleSection = useCallback((sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionKey)) {
        next.delete(sectionKey)
      } else {
        next.add(sectionKey)
      }
      persistCollapsedSections(next)
      return next
    })
  }, [persistCollapsedSections])

  const gridSectionKeys = useMemo(() => {
    if (view === 'one_time_items') return []
    return gridBandSectionKeys(schema, view, draft)
  }, [schema, view, draft])

  const expandAllSections = useCallback(() => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      for (const key of gridSectionKeys) {
        next.delete(key)
      }
      persistCollapsedSections(next)
      return next
    })
  }, [gridSectionKeys, persistCollapsedSections])

  const collapseAllSections = useCallback(() => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      for (const key of gridSectionKeys) {
        next.add(key)
      }
      persistCollapsedSections(next)
      return next
    })
  }, [gridSectionKeys, persistCollapsedSections])

  const openValidationDialog = useCallback(() => {
    if (!evaluated || !history) {
      setValidationErrors([
        {
          code: 'missing_data',
          line_key: null,
          view: null,
          t: null,
          message: 'Model data is not available for validation',
        },
      ])
      setValidationWarnings([])
      setValidationOpen(true)
      return
    }
    const skipRecipeMismatch =
      rollForwardSession?.choice === 'keep' ||
      (selectedCase?.outputs_stale === true && rollForwardSession?.choice !== 'reanchor')
    const result = validateCase(
      evaluated,
      draft,
      history,
      thresholdsFromSettings(model),
      {
        ...validationOptionsFromModel(model, draft.cash_policy),
        skipRecipeMismatch,
      },
    )
    setValidationErrors(
      result.errors.map((e) => ({
        code: e.code,
        line_key: e.line_key,
        view: e.view,
        t: e.t,
        message: e.message,
        label: e.label,
        section: e.section,
      })),
    )
    setValidationWarnings(
      result.warnings.map((w) => ({
        code: w.code,
        line_key: w.line_key,
        view: w.view,
        t: w.t,
        message: w.message,
        label: w.label,
        section: w.section,
      })),
    )
    setValidationOpen(true)
  }, [draft, evaluated, history, model, rollForwardSession, selectedCase?.outputs_stale])

  const handleSaveClick = useCallback(() => {
    openValidationDialog()
  }, [openValidationDialog])

  const runUploadHandOff = useCallback(async () => {
    const state = useProjectionsStore.getState()
    if (!state.evaluated || !state.history) {
      openValidationDialog()
      return
    }
    if (
      uploadValidationIsClean(state.evaluated, state.draft, state.history, state.model)
    ) {
      const result = await save(false)
      if (result.ok) {
        const handOff = uploadHandOffRef.current
        const caseName = useProjectionsStore.getState().selectedCase?.name ?? 'Case'
        if (handOff) {
          toast.success(
            `Case '${caseName}' ${handOff.wasCreate ? 'created' : 'overridden'} from ${handOff.fileName}`,
          )
          uploadHandOffRef.current = null
        } else {
          toast.success('Case saved')
        }
        setValidationOpen(false)
        return
      }
      if (result.kind === 'validation') {
        setValidationErrors(result.errors)
        setValidationWarnings(result.warnings)
        setValidationOpen(true)
        return
      }
      if (result.kind === 'concurrency') {
        setConcurrencyOpen(true)
        return
      }
      toast.error(result.message)
      return
    }
    const selected = state.selectedCase
    const skipRecipeMismatch =
      rollForwardSession?.choice === 'keep' ||
      (selected?.outputs_stale === true && rollForwardSession?.choice !== 'reanchor')
    const result = validateCase(
      state.evaluated,
      state.draft,
      state.history,
      thresholdsFromSettings(state.model),
      {
        ...validationOptionsFromModel(state.model, state.draft.cash_policy),
        skipRecipeMismatch,
      },
    )
    setValidationErrors(
      result.errors.map((e) => ({
        code: e.code,
        line_key: e.line_key,
        view: e.view,
        t: e.t,
        message: e.message,
        label: e.label,
        section: e.section,
      })),
    )
    setValidationWarnings(
      result.warnings.map((w) => ({
        code: w.code,
        line_key: w.line_key,
        view: w.view,
        t: w.t,
        message: w.message,
        label: w.label,
        section: w.section,
      })),
    )
    setValidationOpen(true)
  }, [openValidationDialog, rollForwardSession, save])

  const handleSelectKeep = useCallback(() => {
    if (rollForwardSession == null || rollForwardSession.choice === 'keep') {
      return
    }
    discard()
    setRollForwardSession({ ...rollForwardSession, choice: 'keep' })
  }, [discard, rollForwardSession])

  const handleSelectReanchor = useCallback(() => {
    if (rollForwardSession == null || rollForwardSession.choice === 'reanchor') {
      return
    }
    if (dirty) {
      discard()
    }
    reanchorRecipes()
    setRollForwardSession({ ...rollForwardSession, choice: 'reanchor' })
  }, [discard, dirty, reanchorRecipes, rollForwardSession])

  const handleSelectHold = useCallback(() => {
    const sessionChoice = rollForwardSession?.choice ?? 'keep'
    if (dirty && sessionChoice !== 'hold') {
      discard()
    }
    setHoldValuesOpen(true)
  }, [discard, dirty, rollForwardSession?.choice])

  const handleValidationConfirm = useCallback(
    async (acknowledgeWarnings: boolean) => {
      const result = await save(acknowledgeWarnings)
      if (result.ok) {
        const handOff = uploadHandOffRef.current
        const caseName = useProjectionsStore.getState().selectedCase?.name ?? 'Case'
        if (handOff) {
          toast.success(
            `Case '${caseName}' ${handOff.wasCreate ? 'created' : 'overridden'} from ${handOff.fileName}`,
          )
          uploadHandOffRef.current = null
        } else {
          toast.success('Case saved')
        }
        setValidationOpen(false)
        return
      }
      if (result.kind === 'validation') {
        setValidationErrors(result.errors)
        setValidationWarnings(result.warnings)
        return
      }
      if (result.kind === 'concurrency') {
        setValidationOpen(false)
        setConcurrencyOpen(true)
        return
      }
      toast.error(result.message)
      setValidationOpen(false)
    },
    [save],
  )

  const handleImport = useCallback(async () => {
    const result = await importFromCgm()
    if (result.ok) {
      invalidateFloorAnalysisCache()
      const month = result.result.last_closed_month?.slice(0, 7) ?? ''
      let message = `Imported CGM data for ${month} (${result.result.warning_count} warnings)`
      if (result.result.labor_months != null) {
        message += `. Labor history: ${result.result.labor_months} months, ${result.result.labor_warning_count ?? 0} warnings`
      }
      if (result.result.labor_error) {
        message += `. Labor history failed: ${result.result.labor_error}`
      }
      toast.success(message)
      setExportConflict(null)
      setExportConflictCode(null)
      const rf = result.result.roll_forward
      if (rf != null && rf.k_months >= 1) {
        setRollForwardSession({
          summary: rf,
          lastClosedMonth: result.result.last_closed_month,
          choice: 'keep',
        })
        setRollForwardDialogOpen(true)
      }
      return
    }
    if (result.kind === 'drift') {
      return
    }
    if (result.kind === 'error') {
      toast.error(result.message)
    }
  }, [importFromCgm])

  const handleRollForwardNow = useCallback(async () => {
    if (selectedCaseId == null) {
      return
    }
    try {
      const summary = await rollForward([selectedCaseId])
      if (summary.failures.length > 0) {
        toast.error(summary.failures.map((f) => f.error).join('; '))
      } else {
        toast.success(
          `${summary.cases_processed} cases rolled forward, ${summary.snapshots_written} snapshots`,
        )
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Roll-forward failed'))
    }
  }, [rollForward, selectedCaseId])

  const handleReviewRollForward = useCallback(async () => {
    if (rollForwardSession != null) {
      setRollForwardDialogOpen(true)
      return
    }
    const importId = model?.latest_import?.id
    const closedMonth = model?.latest_import?.last_closed_month
    if (importId == null || closedMonth == null) {
      toast.error('No roll-forward summary available.')
      return
    }
    try {
      const runs = await listRollForwardRuns(5)
      const matched = runs.find((run) => run.import_id === importId)
      const run = matched ?? runs[0]
      if (run?.summary == null) {
        toast.error('No roll-forward summary available.')
        return
      }
      setRollForwardSession({
        summary: run.summary,
        lastClosedMonth: closedMonth,
        choice: 'keep',
      })
      setRollForwardDialogOpen(true)
    } catch (error) {
      toast.error(formatApiError(error, 'Could not load roll-forward summary'))
    }
  }, [model?.latest_import?.id, model?.latest_import?.last_closed_month, rollForwardSession])

  const handleHoldApply = useCallback(
    async (view: View, keys: string[]) => {
      const n = await holdPreRollValues(view, keys)
      toast.success(`${n} lines now hold their pre-roll projected values — review and Save`)
      setRollForwardSession((prev) => (prev != null ? { ...prev, choice: 'hold' } : null))
    },
    [holdPreRollValues],
  )

  const handleOpenNote = useCallback(
    (
      cellKey: string,
      position: { x: number; y: number },
      context: string,
      options?: {
        variant?: 'cell' | 'line'
        computedMethodology?: string | null
        recipeRationale?: string
      },
    ) => {
      setNoteTarget({
        cellKey,
        position,
        context,
        variant: options?.variant ?? 'cell',
        computedMethodology: options?.computedMethodology,
        recipeRationale: options?.recipeRationale,
      })
    },
    [],
  )

  const handleSaveNote = useCallback(
    async (text: string) => {
      if (!noteTarget) return
      const result = await upsertNote(noteTarget.cellKey, text)
      if (!result.ok) {
        toast.error(result.message)
      }
    },
    [noteTarget, upsertNote],
  )

  const handleSaveMethodology = useCallback(
    async (text: string) => {
      if (!noteTarget) return
      const methodologyKey = noteTarget.cellKey.replace(/:label$/, ':methodology')
      const result = await upsertNote(methodologyKey, text)
      if (!result.ok) {
        toast.error(result.message)
      }
    },
    [noteTarget, upsertNote],
  )

  const handleExportClick = useCallback(async () => {
    if (selectedCaseId == null || selectedCase == null) return
    setExporting(true)
    setExportConflict(null)
    setExportConflictCode(null)
    setExportConflictDiff(null)
    try {
      const dryRun = await exportCase(selectedCaseId, {
        dry_run: true,
        expected_updated_at: selectedCase.updated_at,
      })
      setExportDryRun(dryRun)
    } catch (error) {
      const conflict = isExportConflict(error)
      if (conflict) {
        setExportDryRun({ ranges: [], non_empty: [], first_col: null })
        setExportConflictCode(conflict.code)
        setExportConflict(conflict.detail)
        setExportConflictDiff(conflict.diff ?? null)
      } else {
        toast.error(formatApiError(error, 'Export dry-run failed'))
      }
    } finally {
      setExporting(false)
    }
  }, [selectedCase, selectedCaseId])

  const handleExportConfirm = useCallback(async () => {
    if (selectedCaseId == null || selectedCase == null) return
    setExporting(true)
    setExportConflict(null)
    setExportConflictCode(null)
    setExportConflictDiff(null)
    try {
      const result = await exportCase(selectedCaseId, {
        dry_run: false,
        expected_updated_at: selectedCase.updated_at,
      })
      const cellCount = result.ranges.reduce(
        (sum, range) => sum + (range.last_row - range.first_row + 1) * 36,
        0,
      )
      toast.success(`Exported ${cellCount} cells starting at ${result.first_col ?? '—'}`)
      await refreshExports(selectedCaseId)
      setExportDryRun(null)
    } catch (error) {
      const conflict = isExportConflict(error)
      if (conflict) {
        setExportConflictCode(conflict.code)
        setExportConflict(conflict.detail)
        setExportConflictDiff(conflict.diff ?? null)
      } else {
        toast.error(formatApiError(error, 'Export failed'))
      }
    } finally {
      setExporting(false)
    }
  }, [refreshExports, selectedCase, selectedCaseId])

  const handleReloadKeepDraft = useCallback(async () => {
    await adoptServerTimestamp()
    setConcurrencyOpen(false)
    openValidationDialog()
  }, [adoptServerTimestamp, openValidationDialog])

  const handleSelectCase = useCallback(
    (id: number) => {
      if (id === selectedCaseId) {
        return
      }
      if (dirty) {
        setPendingAction({ type: 'selectCase', caseId: id })
        return
      }
      void selectCase(id)
    },
    [dirty, selectCase, selectedCaseId],
  )

  const handleUploadJsonClick = useCallback(() => {
    if (dirty) {
      setPendingAction({ type: 'uploadJson' })
      return
    }
    setUploadOpen(true)
  }, [dirty])

  const handleConfirmPendingAction = useCallback(() => {
    if (pendingAction == null) {
      return
    }
    if (pendingAction.type === 'selectCase') {
      void selectCase(pendingAction.caseId)
    } else {
      discard()
      setUploadOpen(true)
    }
    setPendingAction(null)
  }, [discard, pendingAction, selectCase])

  const handleLoadedCaseFile = useCallback(
    async (parsed: ParsedCaseFile, fileName: string, wasCreate: boolean) => {
      const result = await loadCaseFromFile(parsed)
      if (!result.ok) {
        toast.error(result.message)
        return
      }
      setUploadOpen(false)
      setOverrideParsed(null)
      setOverrideFileName(null)
      if (result.hydrationProblems.length > 0) {
        toast.error(result.hydrationProblems.join(' '))
      }
      uploadHandOffRef.current = { fileName, wasCreate }
      await runUploadHandOff()
    },
    [loadCaseFromFile, runUploadHandOff],
  )

  const handleSetDriverType = useCallback(
    (line: string, type: Parameters<typeof setDriverType>[2]) => {
      if (view === 'one_time_items') return
      setDriverType(view, line, type)
    },
    [view, setDriverType],
  )

  const handleSetDriverParams = useCallback(
    (line: string, type: Parameters<typeof setDriverParams>[2], params: Parameters<typeof setDriverParams>[3]) => {
      if (view === 'one_time_items') return
      setDriverParams(view, line, type, params)
    },
    [view, setDriverParams],
  )

  const handleSetPctRef = useCallback(
    (line: string, ref: string) => {
      if (view === 'one_time_items') return
      setPctOfLineRef(view, line, ref)
    },
    [view, setPctOfLineRef],
  )

  const handleSetValue = useCallback(
    (line: string, t: number, value: number | null) => {
      if (view === 'one_time_items') return
      setValue(view, line, t, value)
    },
    [view, setValue],
  )

  const handleApplyFromColumn = useCallback(
    (line: string, startT: number, mode?: 'keep_shape' | 'replace_flat') => {
      if (view === 'one_time_items') return
      applyFromColumn(view, line, startT, mode)
    },
    [view, applyFromColumn],
  )

  const handleClearOverride = useCallback(
    (line: string, t: number) => {
      if (view === 'one_time_items') return
      clearOverride(view, line, t)
    },
    [view, clearOverride],
  )

  const handleSetOverrideReason = useCallback(
    (line: string, t: number, reason: string | null) => {
      if (view === 'one_time_items') return
      setOverrideReason(view, line, t, reason)
    },
    [view, setOverrideReason],
  )

  const handleClearDriverValues = useCallback(
    (line: string) => {
      if (view === 'one_time_items') return
      clearDriverValues(view, line)
    },
    [view, clearDriverValues],
  )

  const setHistoryMonthsValue = (months: HistoryMonths) => setHistoryMonths(months)

  const showInitialLoading = loading && selectedCaseId == null
  const gridView: View = view === 'one_time_items' ? 'river_oaks' : view
  const holdEnabled =
    selectedCase?.pre_rollforward_outputs != null && caseEditable
  const rolledForwardMonthLabel = formatMonthLabel(monthLabel(lastClosedMonth, 0))
  const outputsStaleBanner =
    selectedCase?.outputs_stale === true
      ? `Rolled forward to ${rolledForwardMonthLabel} actuals on ${(selectedCase.rolled_forward_at ?? '').slice(0, 10)}. Projected balances now start from ${rolledForwardMonthLabel}'s actual balance sheet. Review, then Save to refresh this case's saved outputs. Export and Case summary are unavailable until then.`
      : null

  return (
    <AccountingToolPage
      title="Projections"
      description="Cash-flow projection model"
      scrollContent={false}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-col">
        <div data-projections-model-bar>
        <ModelBar
          cases={cases}
          selectedCaseId={selectedCaseId}
          selectedExpiresOn={selectedCase?.expires_on}
          caseExpired={caseExpired}
          dirty={dirty}
          pageCanEdit={pageCanEdit}
          caseEditable={caseEditable}
          needsRollForward={needsRollForward}
          saving={saving}
          importing={importing}
          importedAt={model?.latest_import?.imported_at}
          lastClosedMonth={lastClosedMonth}
          exportEnabled={exportEnabled}
          exportDisabledTitle={exportDisabledTitle}
          schemaStale={schemaStale}
          exporting={exporting}
          onSelectCase={handleSelectCase}
          onNew={() => {
            setNewCaseError(null)
            setShowNewCase(true)
          }}
          onEditCase={() => {
            setEditCaseError(null)
            setShowEditCase(true)
          }}
          onDuplicate={() => {
            setDuplicateCaseError(null)
            setShowDuplicateCase(true)
          }}
          onDelete={() => setShowDeleteCase(true)}
          onSave={handleSaveClick}
          onDiscard={discard}
          onImport={() => void handleImport()}
          onSummary={() => setSummaryOpen(true)}
          onExport={() => void handleExportClick()}
          onUploadJson={handleUploadJsonClick}
          schema={schema}
          history={history}
          model={model}
          selectedCase={selectedCase}
          saved={saved}
          notes={notes}
          recipeLineCount={recipeLineCount(draft)}
          holdEnabled={holdEnabled}
          onReanchorRecipes={() => setReanchorConfirmOpen(true)}
          onHoldProjectedValues={() => setHoldValuesOpen(true)}
        />
        </div>

        {schemaStale ? (
          <WarningBanner
            message="The CGM sheet layout changed since the last import — run Update Historicals before trusting actuals or exporting."
            className="mt-3"
          />
        ) : null}

        {outputsStaleBanner ? (
          <WarningBanner
            message={outputsStaleBanner}
            className="mt-3"
            actionLabel="Review roll-forward"
            onAction={() => void handleReviewRollForward()}
          />
        ) : null}

        {needsRollForward ? (
          <div className="mt-3 flex flex-col gap-2">
            <WarningBanner message="This case is still based on a previous import — roll-forward has not run or failed." />
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => void handleRollForwardNow()}
            >
              Roll forward now
            </Button>
          </div>
        ) : null}

        {importBlock ? (
          <WarningBanner message={importBlock.detail} className="mt-3" />
        ) : null}

        {cashPolicyBreachMessage ? (
          <WarningBanner message={cashPolicyBreachMessage} className="mt-3" />
        ) : null}

        {scheduleHealedNotice ? (
          <ScheduleHealedBanner
            to={scheduleHealedNotice.to}
            healed={scheduleHealedNotice.healed}
            reason={scheduleHealedNotice.reason}
            showRefresh={showScheduleHealRefresh}
            refreshing={healRefreshing}
            onRefresh={() => void refreshHealedSchedule()}
          />
        ) : null}

        {loadError ? (
          <div
            className="mt-3 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-950"
            role="alert"
          >
            <span className="min-w-0 flex-1 text-sm leading-relaxed">{loadError}</span>
          </div>
        ) : null}

        <div className="mt-4" data-projections-kpi-strip>
        <KpiStrip
          evaluated={evaluated}
          lastClosedMonth={lastClosedMonth}
          onOpenCashPolicy={() => setCashPolicyOpen(true)}
          policyEnabled={draft.cash_policy?.enabled === true}
          amountPerPeriod={draft.cash_policy?.distribution.amount_per_period ?? null}
          cadence={draft.cash_policy?.distribution.cadence ?? null}
        />
        </div>

        <div
          data-projections-tabs-row
          className="mt-5 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200"
        >
          <UnderlineTabNav
            tabs={VIEW_TABS}
            activeId={view}
            onChange={(id) => setView(id as ProjectionsView)}
            ariaLabel="Projection view"
          />
          {view !== 'one_time_items' ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <GridViewMenu
                historyMonths={historyMonths}
                onHistoryMonthsChange={setHistoryMonthsValue}
                onExpandAll={expandAllSections}
                onCollapseAll={collapseAllSections}
              />
              {hasSnapshots ? (
                <GridCompareMenu
                  compareMode={compareMode}
                  onCompareModeChange={setCompareMode}
                  compareColumns={compareColumns}
                  onToggleCompareColumn={toggleCompareColumn}
                  visibleSnapshotMonths={visibleSnapshotMonths}
                  visibleSnapshotCount={visibleSnapshotCount}
                  onOpenAccuracyReport={() => setAccuracyOpen(true)}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        </div>

        {showInitialLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {view === 'one_time_items' ? (
              <OneTimeItems
                schema={schema}
                draft={draft}
                lastClosedMonth={lastClosedMonth}
                canEdit={caseEditable}
                onAdd={addOneTimeItem}
                onUpdate={updateOneTimeItem}
                onRemove={removeOneTimeItem}
              />
            ) : (
              <ProjectionsGrid
                ref={gridRef}
                schema={schema}
                view={gridView}
                draft={draft}
                evaluated={evaluated}
                history={history}
                historyMonths={historyMonths}
                lastClosedMonth={lastClosedMonth}
                canEdit={caseEditable}
                notes={notes}
                onOpenNote={handleOpenNote}
                onSetDriverType={handleSetDriverType}
                onSetDriverParams={handleSetDriverParams}
                onSetPctRef={handleSetPctRef}
                onSetValue={handleSetValue}
                onApplyFromColumn={handleApplyFromColumn}
                onClearOverride={caseEditable ? handleClearOverride : undefined}
                onSetOverrideReason={caseEditable ? handleSetOverrideReason : undefined}
                onClearDriverValues={handleClearDriverValues}
                onToggleConsolidatedOnly={toggleConsolidatedOnly}
                collapsedSections={collapsedSections}
                onToggleSection={toggleSection}
                partnerLabels={model?.partner_labels ?? null}
                compare={gridCompare}
              />
            )}
          </>
        )}

        {switchingCase ? (
          <div className="pointer-events-none absolute inset-0 z-30 flex items-start justify-center bg-white/40 pt-16">
            <p className="rounded-md bg-white px-3 py-2 text-sm text-gray-600 shadow">Switching case…</p>
          </div>
        ) : null}
      </div>

      {showNewCase ? (
        <NewCaseDialog
          saving={saving}
          error={newCaseError}
          onCancel={() => {
            setShowNewCase(false)
            setNewCaseError(null)
          }}
          onConfirm={(name) => {
            void createCase(name).then((result) => {
              if (result.ok) {
                setShowNewCase(false)
                setNewCaseError(null)
              } else if (result.kind === 'conflict') {
                setNewCaseError(result.message)
              }
            })
          }}
        />
      ) : null}

      {showDuplicateCase && selectedCase ? (
        <DuplicateCaseDialog
          caseName={selectedCase.name}
          saving={saving}
          error={duplicateCaseError}
          onCancel={() => {
            setShowDuplicateCase(false)
            setDuplicateCaseError(null)
          }}
          onConfirm={(name) => {
            void duplicateCase(name).then((result) => {
              if (result.ok) {
                setShowDuplicateCase(false)
                setDuplicateCaseError(null)
              } else if (result.kind === 'conflict') {
                setDuplicateCaseError(result.message)
              }
            })
          }}
        />
      ) : null}

      {showEditCase && selectedCase ? (
        <EditCaseDialog
          initialName={selectedCase.name}
          initialDescription={selectedCase.description}
          initialExpiresOn={selectedCase.expires_on}
          saving={saving}
          error={editCaseError}
          onCancel={() => {
            setShowEditCase(false)
            setEditCaseError(null)
          }}
          onConfirm={(values) => {
            void updateCaseMeta(values).then((result) => {
              if (result.ok) {
                setShowEditCase(false)
                setEditCaseError(null)
                toast.success('Case updated')
              } else if (result.kind === 'concurrency') {
                setEditCaseError('Someone else saved this case. Reload and try again.')
              } else {
                setEditCaseError(result.message)
              }
            })
          }}
        />
      ) : null}

      {showDeleteCase && selectedCase ? (
        <DeleteCaseDialog
          caseName={selectedCase.name}
          saving={saving}
          onCancel={() => setShowDeleteCase(false)}
          onConfirm={() => {
            void deleteCase().then(() => setShowDeleteCase(false))
          }}
        />
      ) : null}

      {reanchorConfirmOpen && selectedCase ? (
        <DiscardConfirm
          title="Re-anchor recipes?"
          message={`Re-materialise ${recipeLineCount(draft)} recipe-driven lines on ${selectedCase.name} from actuals through ${rolledForwardMonthLabel}? Their projected values will change; Save to keep the result or Discard to revert.`}
          confirmLabel="Re-anchor"
          onCancel={() => setReanchorConfirmOpen(false)}
          onConfirm={() => {
            const n = reanchorRecipes()
            toast.success(`${n} lines re-anchored — review and Save`)
            setReanchorConfirmOpen(false)
          }}
        />
      ) : null}

      {holdValuesOpen && selectedCase ? (
        <HoldProjectedValuesDialog
          schema={schema}
          draft={draft}
          selectedCase={selectedCase}
          onClose={() => setHoldValuesOpen(false)}
          onApply={(viewKey, keys) => void handleHoldApply(viewKey, keys)}
        />
      ) : null}

      {rollForwardDialogOpen && rollForwardSession ? (
        <RollForwardResultDialog
          summary={rollForwardSession.summary}
          lastClosedMonth={rollForwardSession.lastClosedMonth}
          recipeCount={recipeLineCount(draft)}
          failures={rollForwardSession.summary.failures}
          importRevertible={model?.latest_import?.import_revertible === true}
          choice={rollForwardSession.choice}
          onSelectKeep={handleSelectKeep}
          onSelectReanchor={handleSelectReanchor}
          onSelectHold={handleSelectHold}
          onClose={() => setRollForwardDialogOpen(false)}
          onDiscardImport={() => setDiscardImportConfirmOpen(true)}
          onRetry={(ids) => {
            void rollForward(ids).then((summary) => {
              if (summary.k_months >= 1) {
                setRollForwardSession((prev) => ({
                  summary,
                  lastClosedMonth:
                    model?.latest_import?.last_closed_month ??
                    prev?.lastClosedMonth ??
                    rollForwardSession.lastClosedMonth,
                  choice: prev?.choice ?? 'keep',
                }))
              }
              if (summary.failures.length > 0) {
                toast.error(summary.failures.map((f) => f.error).join('; '))
              } else {
                toast.success(
                  `${summary.cases_processed} cases rolled forward, ${summary.snapshots_written} snapshots`,
                )
                setRollForwardDialogOpen(false)
              }
            })
          }}
        />
      ) : null}

      {discardImportConfirmOpen && rollForwardSession ? (
        <DiscardConfirm
          title="Discard Update Historicals?"
          message={`This restores actuals through ${formatMonthLabel(
            monthLabel(rollForwardSession.lastClosedMonth, -rollForwardSession.summary.k_months),
          )} and puts every rolled-forward case back as it was. Snapshots for the newly closed month are deleted. Saves made after this update on those cases are undone. You cannot restore this update afterwards.`}
          confirmLabel="Discard Update Historicals"
          confirmVariant="danger"
          onCancel={() => setDiscardImportConfirmOpen(false)}
          onConfirm={() => {
            void revertImport()
              .then((result) => {
                toast.success(
                  `Restored actuals through ${formatMonthLabel(result.last_closed_month)}`,
                )
                setDiscardImportConfirmOpen(false)
                setRollForwardDialogOpen(false)
                setRollForwardSession(null)
              })
              .catch((error: unknown) => {
                toast.error(formatApiError(error, 'Could not discard Update Historicals'))
              })
          }}
        />
      ) : null}

      {pendingAction != null ? (
        <DiscardConfirm
          title="Discard unsaved changes?"
          message={
            pendingAction.type === 'uploadJson'
              ? 'Uploading a case file will discard your unsaved edits to the current case.'
              : 'Switching cases will discard your unsaved edits to the current case.'
          }
          confirmLabel={
            pendingAction.type === 'uploadJson' ? 'Discard and continue' : 'Discard and switch'
          }
          onCancel={() => setPendingAction(null)}
          onConfirm={handleConfirmPendingAction}
        />
      ) : null}

      {uploadOpen ? (
        <UploadCaseDialog
          open={uploadOpen}
          schema={schema}
          model={model}
          cases={cases}
          onClose={() => setUploadOpen(false)}
          onContinueCreate={(parsed, fileName) => {
            void handleLoadedCaseFile(parsed, fileName, true)
          }}
          onContinueOverride={(parsed, fileName) => {
            setOverrideParsed(parsed)
            setOverrideFileName(fileName)
          }}
        />
      ) : null}

      {overrideParsed && overrideFileName ? (
        <OverrideCaseDialog
          caseName={
            overrideParsed.target.kind === 'override' ? overrideParsed.target.name : ''
          }
          fileName={overrideFileName}
          targetCase={cases.find(
            (caseItem) =>
              overrideParsed.target.kind === 'override' &&
              caseItem.name === overrideParsed.target.name,
          ) ?? null}
          selectedCase={selectedCase}
          onCancel={() => {
            setOverrideParsed(null)
            setOverrideFileName(null)
          }}
          onConfirm={() => {
            void handleLoadedCaseFile(overrideParsed, overrideFileName, false)
          }}
        />
      ) : null}

      {validationOpen ? (
        <SaveValidationDialog
          errors={validationErrors}
          warnings={validationWarnings}
          onCancel={() => setValidationOpen(false)}
          onConfirm={(acknowledgeWarnings) => void handleValidationConfirm(acknowledgeWarnings)}
        />
      ) : null}

      {concurrencyOpen ? (
        <ConcurrencyDialog
          saving={saving}
          onCancel={() => setConcurrencyOpen(false)}
          onReload={() => {
            void reloadFromServer().then(() => setConcurrencyOpen(false))
          }}
          onReloadKeepDraft={() => void handleReloadKeepDraft()}
        />
      ) : null}

      {noteTarget ? (
        <NotePopover
          cellKey={noteTarget.cellKey}
          initialText={notes[noteTarget.cellKey] ?? ''}
          position={noteTarget.position}
          variant={noteTarget.variant ?? 'cell'}
          initialMethodology={
            notes[noteTarget.cellKey.replace(/:label$/, ':methodology')] ??
            noteTarget.recipeRationale ??
            ''
          }
          computedMethodology={noteTarget.computedMethodology}
          onClose={() => setNoteTarget(null)}
          onSave={handleSaveNote}
          onSaveMethodology={
            noteTarget.variant === 'line' ? handleSaveMethodology : undefined
          }
          readOnly={!pageCanEdit}
        />
      ) : null}

      {accuracyOpen ? (
        <ProjectionAccuracyModal
          open={accuracyOpen}
          onClose={() => setAccuracyOpen(false)}
          initialCaseId={selectedCaseId}
          lastClosedMonth={lastClosedMonth}
          history={history}
          schema={schema}
        />
      ) : null}

      {summaryOpen && selectedCaseId != null ? (
        <CaseSummaryModal
          open={summaryOpen}
          caseId={selectedCaseId}
          caseName={selectedCase?.name ?? 'Case'}
          lastClosedMonth={lastClosedMonth}
          pageCanEdit={pageCanEdit}
          caseSavedAt={selectedCase?.saved_at ?? null}
          onClose={() => setSummaryOpen(false)}
        />
      ) : null}

      {exportDryRun ? (
        <ExportConfirmDialog
          dryRun={exportDryRun}
          lastClosedMonth={lastClosedMonth}
          exporting={exporting}
          conflictCode={exportConflictCode}
          conflictDetail={exportConflict}
          conflictDiff={exportConflictDiff}
          onCancel={() => {
            setExportDryRun(null)
            setExportConflict(null)
            setExportConflictCode(null)
            setExportConflictDiff(null)
          }}
          onConfirm={() => void handleExportConfirm()}
          onImport={() => {
            setExportDryRun(null)
            setExportConflict(null)
            setExportConflictCode(null)
            setExportConflictDiff(null)
            void handleImport()
          }}
          onSave={handleSaveClick}
        />
      ) : null}

      {cashPolicyOpen && model && history ? (
        <CashPolicyModal
          open={cashPolicyOpen}
          canEdit={caseEditable}
          caseName={selectedCase?.name ?? 'Case'}
          draft={draft}
          storeDirty={dirty}
          schema={schema}
          history={history}
          model={model}
          lastClosedMonth={lastClosedMonth}
          onClose={() => setCashPolicyOpen(false)}
          onSetCashPolicy={setCashPolicy}
        />
      ) : null}

      {importDrift ? (
        <DrawerShell title="Import drift detected" onClose={clearImportDrift}>
          <pre className="whitespace-pre-wrap text-xs text-gray-700">
            {JSON.stringify(importDrift.diff, null, 2)}
          </pre>
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={clearImportDrift}>
              Close
            </Button>
          </div>
        </DrawerShell>
      ) : null}
    </AccountingToolPage>
  )
}
